import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useMoveCandidateStageV2 } from '@/hooks/useCandidatesV2';

const STAGES = ['applied','resume_parsed','eligible','exam_assigned','exam_completed','shortlisted','interview_scheduled','offer','hired','rejected'];

const STAGE_LABELS = {
  applied: 'Applied', resume_parsed: 'Resume Parsed', eligible: 'Eligible',
  exam_assigned: 'Exam Assigned', exam_completed: 'Exam Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  offer: 'Offer', hired: 'Hired', rejected: 'Rejected',
};

const STAGE_HEADER_COLORS = {
  applied: 'bg-gray-200 text-gray-700',
  resume_parsed: 'bg-blue-100 text-blue-700',
  eligible: 'bg-teal-100 text-teal-700',
  exam_assigned: 'bg-purple-100 text-purple-700',
  exam_completed: 'bg-indigo-100 text-indigo-700',
  shortlisted: 'bg-orange-100 text-orange-700',
  interview_scheduled: 'bg-yellow-100 text-yellow-700',
  offer: 'bg-green-100 text-green-700',
  hired: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const AVATAR_BG = [
  'bg-blue-400', 'bg-green-400', 'bg-purple-400', 'bg-orange-400',
  'bg-pink-400', 'bg-teal-400', 'bg-indigo-400', 'bg-red-400',
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
};

const getAvatarBg = (name) => {
  if (!name) return AVATAR_BG[0];
  return AVATAR_BG[name.charCodeAt(0) % AVATAR_BG.length];
};

const groupByStage = (candidates) => {
  const cols = {};
  STAGES.forEach((s) => { cols[s] = []; });
  (candidates || []).forEach((c) => {
    const stage = c.stage || 'applied';
    if (cols[stage]) cols[stage].push(c);
    else cols['applied'].push(c);
  });
  return cols;
};

const CandidateCard = ({ candidate, index }) => {
  const fullName = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ');
  return (
    <Draggable draggableId={candidate._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white rounded-lg border p-3 mb-2 shadow-sm cursor-grab active:cursor-grabbing transition-shadow ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-300' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${getAvatarBg(fullName)}`}>
              {getInitials(fullName)}
            </div>
            <span className="font-medium text-sm text-gray-800 truncate">{fullName || 'Unnamed'}</span>
          </div>
          {candidate.currentTitle && (
            <p className="text-xs text-gray-500 truncate">{candidate.currentTitle}</p>
          )}
          {candidate.currentCompany && (
            <p className="text-xs text-gray-400 truncate">{candidate.currentCompany}</p>
          )}
          {candidate.lastActivityAt && (
            <p className="text-xs text-gray-300 mt-1.5">
              {format(new Date(candidate.lastActivityAt), 'MMM dd')}
            </p>
          )}
        </div>
      )}
    </Draggable>
  );
};

const CandidatePipelineBoardV2 = ({ candidates = [], onStageChange }) => {
  const [columns, setColumns] = useState(() => groupByStage(candidates));
  const moveStageMutation = useMoveCandidateStageV2();

  useEffect(() => {
    setColumns(groupByStage(candidates));
  }, [candidates]);

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;

    if (sourceStage === destStage) {
      // Reorder within same column
      const col = [...columns[sourceStage]];
      const [moved] = col.splice(source.index, 1);
      col.splice(destination.index, 0, moved);
      setColumns((prev) => ({ ...prev, [sourceStage]: col }));
      return;
    }

    // Move to different stage
    const previousColumns = structuredClone(columns);

    const sourceCol = [...columns[sourceStage]];
    const destCol = [...columns[destStage]];
    const [movedCard] = sourceCol.splice(source.index, 1);
    destCol.splice(destination.index, 0, { ...movedCard, stage: destStage });

    setColumns((prev) => ({ ...prev, [sourceStage]: sourceCol, [destStage]: destCol }));

    moveStageMutation.mutate(
      { id: draggableId, stage: destStage },
      {
        onError: () => {
          setColumns(previousColumns);
          toast.error('Failed to move candidate');
        },
        onSuccess: () => {
          if (onStageChange) onStageChange(draggableId, destStage);
        },
      }
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
        {STAGES.map((stage) => {
          const cards = columns[stage] || [];
          return (
            <div key={stage} className="flex-shrink-0 w-52 flex flex-col">
              {/* Column header */}
              <div className={`rounded-t-lg px-3 py-2 flex items-center justify-between ${STAGE_HEADER_COLORS[stage]}`}>
                <span className="text-xs font-semibold truncate">{STAGE_LABELS[stage]}</span>
                <span className="text-xs font-bold ml-1 bg-white/60 rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                  {cards.length}
                </span>
              </div>

              {/* Droppable area */}
              <Droppable droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 rounded-b-lg p-2 min-h-[400px] transition-colors ${
                      snapshot.isDraggingOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : 'bg-gray-50 border border-t-0 border-gray-200'
                    }`}
                  >
                    {cards.map((c, idx) => (
                      <CandidateCard key={c._id} candidate={c} index={idx} />
                    ))}
                    {provided.placeholder}
                    {cards.length === 0 && !snapshot.isDraggingOver && (
                      <p className="text-xs text-gray-300 text-center mt-4">Drop here</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default CandidatePipelineBoardV2;
